import { useState } from "react";
import { api, STATUS_LABELS } from "@/lib/api";
import { toast } from "sonner";
import { Search } from "lucide-react";

const ORDER = ["quote_accepted", "received", "parts_arrived", "in_repair", "ready_for_pickup", "completed"];

export default function Track() {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!code.trim() || !email.trim()) { toast.error("Enter your ticket and email."); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/bookings/track/${code.trim().toUpperCase()}`, { params: { email: email.trim() } });
      setResult(data);
    } catch {
      toast.error("Ticket not found");
      setResult(null);
    } finally { setLoading(false); }
  };

  return (
    <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="mb-8">
        <div className="overline">Repair tracker</div>
        <h1 className="mt-2 text-3xl md:text-4xl font-bold tracking-tight text-slate-900">Where's my device?</h1>
        <p className="mt-2 text-slate-600">Enter the ticket code from your confirmation email.</p>
      </div>

      <form onSubmit={submit} className="grid sm:grid-cols-3 gap-2 mb-8">
        <input
          data-testid="track-input"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="RN-XXXXXXXX"
          className="sm:col-span-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-mono focus:outline-none focus:border-slate-900"
        />
        <input
          data-testid="track-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email on the booking"
          className="sm:col-span-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:outline-none focus:border-slate-900"
        />
        <button
          data-testid="track-btn"
          disabled={loading}
          className="sm:col-span-1 px-5 py-3 rounded-xl bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-40 inline-flex items-center justify-center gap-2"
        >
          <Search className="w-4 h-4" /> {loading ? "Searching…" : "Track"}
        </button>
      </form>

      {result && (
        <div data-testid="track-result" className="p-6 md:p-8 rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-baseline justify-between">
            <div>
              <div className="overline">Ticket</div>
              <div className="font-mono font-bold text-slate-900 text-lg">{result.ticket_code}</div>
            </div>
            <div className="text-right">
              <div className="overline">Owner</div>
              <div className="font-medium text-slate-900 text-sm">{result.customer_name}</div>
            </div>
          </div>
          <div className="mt-4 text-sm text-slate-700">
            <strong>{result.brand} {result.model_name}</strong> · drop-off {result.dropoff_date} at {result.dropoff_time}
          </div>

          <div className="mt-6">
            <div className="overline mb-3">Progress</div>
            <ol className="space-y-2">
              {ORDER.map((s, i) => {
                const cur = ORDER.indexOf(result.status);
                const done = i <= cur;
                return (
                  <li key={s} className="flex items-center gap-3">
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${done ? "bg-amber-600 text-white" : "bg-slate-100 text-slate-400"}`}>{i + 1}</span>
                    <span className={done ? "text-slate-900 font-medium" : "text-slate-400"}>{STATUS_LABELS[s]}</span>
                  </li>
                );
              })}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}
